"use client";

/**
 * Perf experiment flags — on-device bisection of the iPhone scroll crash.
 *
 * Open the site with ?exp=<flags> (comma-separated) in Safari and the flags
 * persist for the whole browsing session (sessionStorage), surviving SPA
 * navigation. Each flag disables exactly one heavy subsystem, so scrolling
 * the home page under each flag attributes the crash:
 *
 *   ?exp=novu     — no VU meter strips at all
 *   ?exp=nohero   — hero replaced by a plain gradient (like recovery mode)
 *   ?exp=cap6     — horizontal rows capped at 6 cards (any number works)
 *   ?exp=novu,nohero — combine freely; ?exp=off clears all flags
 *
 * The active flags ride along on the telemetry env beacon, so every test
 * run is attributable from /api/diag without manual reporting.
 */

import { useEffect, useState } from "react";

const KEY = "bb-exp";

/** Parse ?exp= into sessionStorage. Called once per boot (layout inline
 *  script also mirrors this for the beacon; this is the React-side read). */
function readAndPersist(): string {
  try {
    const url = new URL(window.location.href);
    const fromUrl = url.searchParams.get("exp");
    if (fromUrl !== null) {
      if (fromUrl === "off" || fromUrl === "") sessionStorage.removeItem(KEY);
      else sessionStorage.setItem(KEY, fromUrl);
    }
    return sessionStorage.getItem(KEY) || "";
  } catch {
    return "";
  }
}

export interface PerfExp {
  has: (flag: string) => boolean;
  /** Numeric value of a capN flag, e.g. cap12 → 12. undefined if not set. */
  cap?: number;
  raw: string;
}

const EMPTY: PerfExp = { has: () => false, raw: "" };

/** Hydration-safe: empty on server + first client render; real flags apply
 *  right after mount (a one-frame delay on an experiment page is fine). */
export function usePerfExp(): PerfExp {
  const [exp, setExp] = useState<PerfExp>(EMPTY);
  useEffect(() => {
    const raw = readAndPersist();
    if (!raw) return;
    const flags = new Set(raw.split(",").map(s => s.trim()).filter(Boolean));
    let cap: number | undefined;
    for (const f of flags) {
      const m = /^cap(\d+)$/.exec(f);
      if (m) cap = Math.max(1, parseInt(m[1], 10));
    }
    setExp({ has: (f: string) => flags.has(f), cap, raw });
  }, []);
  return exp;
}
